/* eslint-disable react-hooks/refs --
 * The fixed-position tooltip reads layout refs during render for placement. This is an
 * intentional migrated viz pattern that works correctly but is not auto-memoizable by
 * React Compiler. */
import '@Components/ScenarioPlanningChart/ScenarioPlanningChart.scss';
import * as R from 'ramda';
import { memo, useEffect, useMemo, useRef, useState, ReactElement } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ErrorBar,
  LabelList,
  Line,
  XAxis,
  YAxis
} from 'recharts';
import { ShadedPattern } from '@Components/FillPatterns/ShadedPattern';
import { ScenarioPlanningBarLabel } from '@Components/ScenarioPlanningChart/ScenarioPlanningBarLabel';
import {
  ScenarioPlanningIndirectBarShape,
  MIN_BAR_HEIGHT,
  ScenarioPlanningDirectBarShape,
  ShapeProps
} from '@Components/ScenarioPlanningChart/ScenarioPlanningBarShape';
import {
  abbreviateScenarioValue,
  getIndirectHeight,
  getSolidHeight,
  isMonetaryMetric,
  ScenarioPlanningChartData,
  ScenarioPlanningChartEntry
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { ScenarioPlanningTooltip } from '@Components/ScenarioPlanningChart/ScenarioPlanningTooltip';
import {
  ScenarioPlanningXAxisTick,
  getTickHeight
} from '@Components/ScenarioPlanningChart/ScenarioPlanningXAxisTick';
import {
  getChannelSeriesColor,
  getModelShade,
  Neutral300,
  Neutral500,
  shouldUsePrimaryPalette,
  Brand100
} from '../../utils/colors';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';

const AXIS_COLOR = Neutral500;
const GRID_COLOR = Neutral300;
const GRID_LINE_WIDTH = 1;
export const MAX_BAR_WIDTH = 200;
const Y_AXIS_FONT_SIZE = 14;
export const MIN_BAR_GAP = 24;
export const MIN_BAR_WIDTH = 100;
const Y_AXIS_INSET = 24;
const Y_AXIS_INNER_WIDTH = 56;
export const Y_AXIS_WIDTH = Y_AXIS_INSET + Y_AXIS_INNER_WIDTH;
export const CHART_SCROLL_AMOUNT = MIN_BAR_WIDTH + MIN_BAR_GAP;

const chooseTooltipLeft = ({
  barRightEdge,
  barWidth,
  tooltipWidth,
  totalChartWidth,
  availableWidth
}: {
  barRightEdge: number;
  barWidth: number;
  tooltipWidth: number;
  totalChartWidth: number;
  availableWidth: number;
}): number => {
  // Before first measurement we don't know the tooltip width; keep the naive right-of-bar
  // placement until the post-render useEffect fills it in.
  if (tooltipWidth <= 0) {
    return barRightEdge;
  }
  const chartAreaWidth = Math.max(totalChartWidth, availableWidth - Y_AXIS_WIDTH);
  if (barRightEdge + tooltipWidth <= chartAreaWidth) {
    return barRightEdge;
  }
  const flippedLeft = barRightEdge - barWidth - tooltipWidth;
  if (flippedLeft >= 0) {
    return flippedLeft;
  }
  const rightGap = chartAreaWidth - barRightEdge;
  return rightGap >= barRightEdge - barWidth ? Math.max(0, chartAreaWidth - tooltipWidth) : 0;
};
const MIN_INDIRECT_SCALE: [number, number][] = [
  [0, 2],
  [12, 4],
  [18, 6],
  [24, 8]
];

interface ScenarioXAxisTickRendererProps {
  chartData: ScenarioPlanningChartEntry[];
  granularity: number;
  width: number;
  // recharts injects the following when it clones the tick element.
  x?: number;
  y?: number;
  height?: number;
  payload?: { index: number };
}

// Hoisted out of ScenarioPlanningChart so recharts doesn't see a brand-new
// component type on every render. recharts clones this element, injecting
// x/y/height/payload, which we forward to the underlying tick.
const ScenarioXAxisTickRenderer = ({
  chartData,
  granularity,
  width,
  x = 0,
  y = 0,
  height = 0,
  payload = { index: 0 }
}: ScenarioXAxisTickRendererProps): ReactElement | null => (
  <ScenarioPlanningXAxisTick
    x={x}
    y={y}
    height={height}
    payload={payload}
    chartData={chartData}
    granularity={granularity}
    width={width}
  />
);

export interface ScenarioPlanningChartProps {
  availableWidth: number;
  channelColorMap?: Record<string, string>;
  data: ScenarioPlanningChartData[];
  isRmmm?: boolean;
  isWaterFall?: boolean;
  outcomeVariable?: string;
  // ==== HROAS: enabled model ids in slot order — position drives each segment's shade of the
  // channel color, identically across bars (and the legend). ====
  segmentModelOrder?: string[];
}

const ScenarioPlanningChartComponent = ({
  availableWidth,
  channelColorMap,
  data = [],
  isRmmm,
  isWaterFall,
  segmentModelOrder,
  outcomeVariable
}: ScenarioPlanningChartProps): ReactElement => {
  const { cid } = useCompanyInfo();
  const useTestPalette = cid === 'tinuititest';
  const containerRef = useRef<HTMLDivElement>(null);
  const chartContentRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const activeIndexRef = useRef<number | undefined>(undefined);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const tooltipHovered = useRef(false);
  const tooltipWidth = useRef(0);
  const leaveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      setContainerHeight(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Bar labels and the Y-axis share one formatter: percentage view converts a bar's raw dollar/count
  // value to "% of total" (against the chart-wide grand total stamped on every entry), while absolute
  // view formats it as a dollar/count amount — identical treatment for both surfaces in either mode.
  const labelFormatter = useMemo(() => {
    if (R.isNil(data) || !data.length) {
      return R.identity;
    }
    if (data[0].showPercentageView) {
      const total = data[0].totalValue;
      return (val: number | string) =>
        total ? `${Math.round((Number(val) / total) * 100)}%` : '0%';
    }
    const monetary = isMonetaryMetric(data[0].metric);
    return (val: number | string) => abbreviateScenarioValue(Number(val), { monetary });
  }, [data]);

  const { barWidth, totalChartWidth } = useMemo(() => {
    if (!data.length || !availableWidth) {
      return { barWidth: 0, totalChartWidth: 0 };
    }
    const chartAreaWidth = availableWidth - Y_AXIS_WIDTH;
    const gaps = MIN_BAR_GAP * Math.max(0, data.length - 1);
    const fittedBarWidth = (chartAreaWidth - gaps) / data.length;
    if (fittedBarWidth >= MIN_BAR_WIDTH && fittedBarWidth <= MAX_BAR_WIDTH) {
      return { barWidth: fittedBarWidth, totalChartWidth: chartAreaWidth };
    }
    if (fittedBarWidth > MAX_BAR_WIDTH) {
      return {
        barWidth: MAX_BAR_WIDTH,
        totalChartWidth: data.length * MAX_BAR_WIDTH + gaps
      };
    }
    return {
      barWidth: MIN_BAR_WIDTH,
      totalChartWidth: data.length * MIN_BAR_WIDTH + gaps
    };
  }, [data.length, availableWidth]);

  const [baseData, granularity, hasScenarioChange] = useMemo(() => {
    const entries: ScenarioPlanningChartEntry[] = [];
    const fallbackColorMap: Record<string, number> = {};
    let fallbackColorIndex = 0;
    // Bars that share a channel share a color, so palette sizing is driven by
    // the distinct channel count, not the total bar count.
    const distinctChannelCount = new Set(data.flatMap(e => (e?.id ? [e.id.split('_')[0]] : [])))
      .size;
    const fallbackUsePrimary = shouldUsePrimaryPalette(distinctChannelCount);
    let scenarioChanged = false;
    let granularityLevel = 1;
    for (let i = 0; i < data.length; i++) {
      const entry = data[i];
      if (!entry) {
        continue;
      }
      if (entry.id) {
        const splitID = entry.id.replace(/_(dc|dg)$/, '').split('_');
        const platform = splitID[1];
        const tactic = splitID[2];
        if (tactic && R.toLower(tactic) !== 'n/a') {
          granularityLevel = 3;
        } else if (platform && R.toLower(platform) !== 'n/a' && granularityLevel !== 3) {
          granularityLevel = 2;
        }
      }
      const displayedTotal = entry.showActualized
        ? entry.directValue + entry.indirectValue
        : entry.directValue;
      if (displayedTotal !== entry.baselineValue) {
        scenarioChanged = true;
      }
      const colorKey = entry && entry.id ? entry.id.split('_')[0] : '';
      let color: string;
      if (channelColorMap && colorKey in channelColorMap) {
        color = channelColorMap[colorKey];
      } else {
        if (R.isNil(fallbackColorMap[colorKey])) {
          fallbackColorMap[colorKey] = fallbackColorIndex;
          fallbackColorIndex++;
        }
        color = getChannelSeriesColor(
          fallbackColorMap[colorKey],
          fallbackUsePrimary,
          useTestPalette
        );
      }
      // Segments and segmentModelOrder come from the same included set, so every id is in the order.
      const segmentFills =
        segmentModelOrder &&
        entry.segments?.map(seg =>
          getModelShade(color, segmentModelOrder.indexOf(seg.id), segmentModelOrder.length)
        );
      const actualizedValue = entry.directValue + entry.indirectValue;
      const prev = i ? entries[i - 1] : undefined;
      const waterFallValue =
        isWaterFall && prev
          ? prev.waterFallValue + getSolidHeight(prev) + getIndirectHeight(prev)
          : 0;
      entries.push({
        ...entry,
        color,
        segmentFills,
        labelValue: entry.showActualized ? actualizedValue : entry.directValue,
        lineValue: waterFallValue + actualizedValue,
        actualizedValue,
        // Percentage view changes only the displayed labels/tooltip (via labelFormatter above) —
        // bar geometry always reflects the raw direct/indirect values, identically to absolute view.
        renderDirectValue: entry.directValue,
        renderIndirectValue: entry.indirectValue,
        waterFallValue
      });
    }
    return [entries, granularityLevel, scenarioChanged];
  }, [channelColorMap, data, isWaterFall, segmentModelOrder, useTestPalette]);

  const tickHeight = getTickHeight(granularity, hasScenarioChange);

  const yDomain = useMemo(() => {
    let yDomainMax = 0;
    const getConfidenceMax = (entry: ScenarioPlanningChartEntry): number => {
      if (Array.isArray(entry.confidenceInterval)) {
        return entry.confidenceInterval[1];
      }
      return R.isNil(entry.confidenceInterval) ? 0 : entry.confidenceInterval;
    };
    for (const entry of baseData) {
      const newValue =
        getSolidHeight(entry) +
        getIndirectHeight(entry) +
        (isWaterFall ? entry.waterFallValue : 0) +
        getConfidenceMax(entry);
      if ([Infinity, -Infinity].includes(newValue)) {
        continue;
      } else if (newValue > yDomainMax) {
        yDomainMax = newValue;
      }
    }
    return [0, Math.round(yDomainMax * 1.2)];
  }, [baseData, isWaterFall]);

  // Layout refinement. baseData entries are frozen once memoized (React 19), so
  // these pixel-aware adjustments are applied to a fresh clone rather than
  // mutated in place. Passes run in order: render-value inflation feeds the
  // height helpers used by the waterfall recumulation, which feeds lineValue.
  const displayedData = useMemo(() => {
    const refined = baseData.map(entry => ({ ...entry }));

    const plotHeight = containerHeight - 10 - tickHeight;
    const hasValidPlot = plotHeight > 0 && Boolean(yDomain[1]);

    // Inflate sub-threshold indirect segments so thin breakout slivers stay
    // visible, trimming the direct segment by the same delta.
    if (hasValidPlot) {
      for (const entry of refined) {
        if (!entry.showBreakout || entry.indirectValue === 0) {
          continue;
        }
        const totalValue = entry.directValue + Math.abs(entry.indirectValue);
        const totalPx = (totalValue / yDomain[1]) * plotHeight;
        let minPx = 0;
        for (const [threshold, px] of MIN_INDIRECT_SCALE) {
          if (totalPx >= threshold) {
            minPx = px;
          }
        }
        if (minPx === 0) {
          continue;
        }
        const absIndirect = Math.abs(entry.indirectValue);
        // Cap the inflated indirect at the bar's total value so the delta we
        // subtract from direct never makes renderDirectValue negative. With the
        // [0, 2] floor this matters for very small bars where 2px in data units
        // would otherwise exceed the bar's whole height.
        const minIndirectValue = Math.min(
          (minPx / plotHeight) * yDomain[1],
          entry.directValue + absIndirect
        );
        if (absIndirect < minIndirectValue) {
          const delta = minIndirectValue - absIndirect;
          entry.renderIndirectValue =
            entry.indirectValue < 0 ? -minIndirectValue : minIndirectValue;
          entry.renderDirectValue = entry.directValue - delta;
        }
      }
    }

    // Recumulate waterFallValue against MIN_BAR_HEIGHT (in data units) so a tiny
    // preceding bar's pixel-clamped height doesn't desync from the next entry's
    // data-unit spacer.
    if (isWaterFall && hasValidPlot) {
      const minDataHeight = (MIN_BAR_HEIGHT / plotHeight) * yDomain[1];
      let cumulative = 0;
      for (const entry of refined) {
        entry.waterFallValue = cumulative;
        const totalHeight = getSolidHeight(entry) + getIndirectHeight(entry);
        cumulative += Math.max(totalHeight, minDataHeight);
      }
    }

    // Pin lineValue to the rendered bar top (under MIN_BAR_HEIGHT) so the
    // ErrorBar whisker caps the bar instead of dangling below it.
    if (hasValidPlot) {
      const minDataHeight = (MIN_BAR_HEIGHT / plotHeight) * yDomain[1];
      for (const entry of refined) {
        const totalHeight = getSolidHeight(entry) + getIndirectHeight(entry);
        entry.lineValue = entry.waterFallValue + Math.max(totalHeight, minDataHeight);
      }
    }

    return refined;
  }, [baseData, containerHeight, isWaterFall, tickHeight, yDomain]);

  const yDomainTicks = useMemo(() => {
    const yMax = yDomain[1];
    const tickCount = 4;
    if (!yMax || yMax <= 0) {
      return [];
    }
    const rawStep = yMax / tickCount;
    const mag = 10 ** Math.floor(Math.log10(rawStep));
    const niceStep = (Math.ceil((rawStep / mag) * 4) / 4) * mag;
    return Array.from({ length: tickCount }, (_, i) => niceStep * (i + 1));
  }, [yDomain]);

  const patterns = useMemo(
    () => (
      <>
        {displayedData.map(
          (entry, index) =>
            entry && (
              <ShadedPattern
                key={`pattern-${index}`}
                color={!entry.isDemandCapture ? 'white' : entry.color}
                backgroundColor={entry.isDemandCapture ? 'white' : `${entry.color}F0`}
                id={R.defaultTo(entry.label, entry.id)?.replace(/ /g, '')}
                visibleTag={false}
              />
            )
        )}
      </>
    ),
    [displayedData]
  );

  useEffect(() => {
    if (tooltipRef.current) {
      tooltipWidth.current = tooltipRef.current.offsetWidth;
    }
  }, [activeIndex]);

  // Capture-phase listener catches scrolls from any inner container (scroll
  // doesn't bubble); the activeIndexRef gate avoids setState on every scroll.
  useEffect(() => {
    const handleAnyScroll = (): void => {
      if (activeIndexRef.current === undefined) {
        return;
      }
      tooltipHovered.current = false;
      activeIndexRef.current = undefined;
      setActiveIndex(undefined);
    };
    window.addEventListener('scroll', handleAnyScroll, { capture: true, passive: true });
    return () => {
      window.removeEventListener('scroll', handleAnyScroll, { capture: true });
    };
  }, []);

  const activeEntry = activeIndex !== undefined ? displayedData[activeIndex] : undefined;
  const fullWidth = Y_AXIS_WIDTH + totalChartWidth;

  return (
    <div
      ref={containerRef}
      className='scenario-planning-chart-container'
      style={{ minWidth: fullWidth || undefined }}
    >
      {containerHeight > 0 && totalChartWidth > 0 && (
        <>
          <div className='y-axis-sticky'>
            <ComposedChart
              // Width is 2× the axis column so a non-zero plot area exists: recharts v3 paints
              // no tick labels when the YAxis width equals the chart width (zero plot). The
              // YAxis occupies the left Y_AXIS_WIDTH (wide enough for the longest tick label,
              // e.g. "$12.75M", which is right-anchored at the axis line); the extra (empty)
              // plot area to its right is clipped by `.yAxisSticky { overflow: hidden }`.
              width={Y_AXIS_WIDTH * 2}
              height={containerHeight}
              margin={{ top: 5, right: 0, bottom: tickHeight + 5, left: 0 }}
              // Bind the chart to the tick values with an invisible bar series so recharts v3
              // realizes the y-scale — with empty data it renders the axis frame but paints
              // no ticks/labels. The explicit domain/ticks drive the displayed values.
              data={yDomainTicks.map(value => ({ value }))}
            >
              <YAxis
                tickFormatter={labelFormatter}
                stroke={AXIS_COLOR}
                tick={{ fontSize: Y_AXIS_FONT_SIZE }}
                tickLine={false}
                ticks={yDomainTicks}
                domain={yDomain}
                type='number'
                width={Y_AXIS_WIDTH}
              />
              <Bar dataKey='value' fill='transparent' isAnimationActive={false} />
            </ComposedChart>
          </div>
          <div ref={chartContentRef} className='chart-content' style={{ width: totalChartWidth }}>
            <div className='chart-grid-gradient' style={{ bottom: tickHeight + 5 }} />
            <div className='pattern-definitions'>{patterns}</div>
            <ComposedChart
              data={displayedData}
              height={containerHeight}
              width={totalChartWidth}
              margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
              onMouseMove={state => {
                clearTimeout(leaveTimer.current);
                if (tooltipHovered.current || state?.activeTooltipIndex === undefined) {
                  return;
                }
                const newIndex = Number(state.activeTooltipIndex);
                if (newIndex !== activeIndexRef.current && state.activeCoordinate) {
                  setTooltipPos({
                    x: state.activeCoordinate.x + barWidth / 2,
                    y: containerHeight / 16
                  });
                }
                activeIndexRef.current = newIndex;
                setActiveIndex(newIndex);
              }}
              onMouseLeave={() => {
                clearTimeout(leaveTimer.current);
                leaveTimer.current = window.setTimeout(() => {
                  if (!tooltipHovered.current) {
                    activeIndexRef.current = undefined;
                    setActiveIndex(undefined);
                  }
                }, 100);
              }}
            >
              <CartesianGrid vertical={false} stroke={GRID_COLOR} strokeWidth={GRID_LINE_WIDTH} />
              <XAxis
                height={tickHeight}
                interval={0}
                stroke={AXIS_COLOR}
                tick={
                  <ScenarioXAxisTickRenderer
                    chartData={displayedData}
                    granularity={granularity}
                    width={barWidth}
                  />
                }
                tickLine={false}
                tickSize={0}
                tickMargin={0}
              />
              <YAxis hide domain={yDomain} ticks={yDomainTicks} type='number' width={0} />
              {isWaterFall && (
                <Bar dataKey='waterFallValue' stackId={0} fill='transparent' barSize={barWidth} />
              )}
              <Bar
                dataKey={getSolidHeight}
                stackId={0}
                barSize={barWidth}
                shape={(shapeProps: unknown) =>
                  ScenarioPlanningDirectBarShape(shapeProps as ShapeProps)
                }
              >
                {displayedData.map((entry, index) => (
                  <Cell key={`baselineValue-${index}`} fill={entry.color} />
                ))}
              </Bar>
              <Bar
                dataKey={getIndirectHeight}
                stackId={0}
                barSize={barWidth}
                shape={(shapeProps: unknown) =>
                  ScenarioPlanningIndirectBarShape(shapeProps as ShapeProps)
                }
              >
                {R.map(
                  entry => (
                    <Cell
                      key={`indirect-${entry.id}`}
                      fill={`url(#shadedStripe${R.defaultTo(entry.label, entry.id)?.replace(
                        / /g,
                        ''
                      )})`}
                    />
                  ),
                  displayedData
                )}
              </Bar>
              <Line
                dataKey='lineValue'
                stroke='none'
                dot={false}
                activeDot={false}
                legendType='none'
                isAnimationActive={false}
              >
                <LabelList
                  dataKey='labelValue'
                  content={<ScenarioPlanningBarLabel formatter={labelFormatter} isRmmm={isRmmm} />}
                />
                <ErrorBar
                  dataKey='confidenceInterval'
                  offset={(3 * barWidth) / 8}
                  strokeWidth={1}
                  stroke={Brand100}
                />
              </Line>
            </ComposedChart>
            {activeIndex !== undefined && (
              <div
                className='hover-indicator'
                style={{
                  left: tooltipPos.x - barWidth / 2,
                  top: 5,
                  height: containerHeight - 10 - tickHeight
                }}
              />
            )}
            {activeEntry && (
              <div
                ref={tooltipRef}
                style={{
                  // Position the tooltip relative to the viewport (fixed) rather than the chart
                  // content. The chart scrolls inside `.charts-column`, which has overflow-x:auto /
                  // overflow-y:hidden and therefore clips an absolutely-positioned tooltip (the
                  // bottom legend was getting cut off). Fixed positioning escapes that clip so the
                  // high z-index can float it above everything. We offset the chart-content-relative
                  // left/top by the content element's current viewport rect (which already accounts
                  // for horizontal scroll).
                  position: 'fixed',
                  left:
                    (chartContentRef.current?.getBoundingClientRect().left ?? 0) +
                    chooseTooltipLeft({
                      barRightEdge: tooltipPos.x,
                      barWidth,
                      tooltipWidth: tooltipWidth.current,
                      totalChartWidth,
                      availableWidth
                    }),
                  top: (chartContentRef.current?.getBoundingClientRect().top ?? 0) + tooltipPos.y,
                  zIndex: 1000,
                  pointerEvents: 'all'
                }}
                onMouseEnter={() => {
                  clearTimeout(leaveTimer.current);
                  tooltipHovered.current = true;
                }}
                onMouseLeave={() => {
                  tooltipHovered.current = false;
                  activeIndexRef.current = undefined;
                  setActiveIndex(undefined);
                }}
              >
                <ScenarioPlanningTooltip
                  payload={[{ payload: activeEntry }]}
                  outcomeVariable={outcomeVariable}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Perf: memoize so scroll-state-driven parent re-renders (chartsColumnWidth / scroll flags) don't
// re-invoke every chart instance when their props (data/channelColorMap/availableWidth) are unchanged.
export const ScenarioPlanningChart = memo(ScenarioPlanningChartComponent);

export type { ScenarioPlanningChartData, ScenarioPlanningChartEntry };
